import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

// Define the base URL for API requests
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9000/api';

// Create API instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include the token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Admin user interface
interface Admin {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  role: 'ADMIN' | 'AGENT';
}

// Auth context interface
interface AuthContextType {
  admin: Admin | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateAdminProfile: (data: { name?: string; phone?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Create the auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('admin_token'));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Computed state for authentication status
  const isAuthenticated = !!admin && !!token;

  // Get admin profile on initial load and when token changes
  useEffect(() => {
    const getAdminProfile = async () => {
      if (!token) {
        setIsInitialized(true);
        return;
      }

      try {
        const response = await api.get('/admin-auth/profile');
        setAdmin(response.data.admin);
      } catch (error) {
        // If the token is invalid, clear it
        console.error('Error fetching admin profile:', error);
        localStorage.removeItem('admin_token');
        setToken(null);
        setAdmin(null);
      } finally {
        setIsInitialized(true);
      }
    };

    getAdminProfile();
  }, [token]);

  // Login function
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await api.post('/admin-auth/login', {
        email,
        password,
      });

      const { token: newToken, admin: adminData } = response.data;

      // Save token to local storage
      localStorage.setItem('admin_token', newToken);
      
      // Update state
      setToken(newToken);
      setAdmin(adminData);
      
      toast.success('Login successful!');
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to login';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
    toast.success('Logged out successfully');
  };

  // Update admin profile
  const updateAdminProfile = async (data: { name?: string; phone?: string }) => {
    setIsLoading(true);
    try {
      const response = await api.put('/admin-auth/profile', data);
      setAdmin(response.data.admin);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      console.error('Update profile error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Change password
  const changePassword = async (currentPassword: string, newPassword: string) => {
    setIsLoading(true);
    try {
      await api.put('/admin-auth/change-password', {
        currentPassword,
        newPassword,
      });
      toast.success('Password changed successfully');
    } catch (error: any) {
      console.error('Change password error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to change password';
      toast.error(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Value to provide to consumers
  const value = {
    admin,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateAdminProfile,
    changePassword,
  };

  // Wait until we've tried to fetch the admin profile before rendering children
  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Export the api for use in other parts of the app
export { api }; 